// src/pages/ResetPassword.js — handles password reset (via token) and first-time setup
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../utils/api';
import { useBranding } from '../context/BrandingContext';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { branding } = useBranding();
  const logoMark = branding.logo_url
    ? <img className="logo-img" src={branding.logo_url} alt={branding.brand_name} />
    : <span className="logo-dot" />;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const strength = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', 'var(--red)', 'var(--amber)', 'var(--blue)', 'var(--green)'][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{logoMark}{branding.brand_name}</div>
          <i className="ti ti-link-off" style={{ fontSize: 36, color: 'var(--red)', display: 'block', margin: '16px auto' }} />
          <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 20 }}>
            This link is missing a reset token. Please use the link sent to you or request a new one.
          </p>
          <Link to="/forgot-password" className="btn btn-primary" style={{ justifyContent: 'center', display: 'flex' }}>Request Reset Link</Link>
          <div style={{ marginTop: 16 }}>
            <Link to="/login" style={{ fontSize: 12, color: 'var(--gray-400)', textDecoration: 'none' }}>Back to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{logoMark}{branding.brand_name}</div>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '20px auto' }}>
            <i className="ti ti-check" style={{ fontSize: 24, color: 'var(--green)' }} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Password set!</div>
          <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 24 }}>
            Your password has been saved successfully. You can now sign in.
          </p>
          <Link to="/login" className="btn btn-primary" style={{ justifyContent: 'center', display: 'flex' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{logoMark}{branding.brand_name}</div>
          <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 4 }}>Set your new password</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={show ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                autoFocus
                style={{ paddingRight: 38 }}
              />
              <button type="button" onClick={() => setShow(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 16, padding: 0 }}>
                <i className={`ti ${show ? 'ti-eye-off' : 'ti-eye'}`} />
              </button>
            </div>
            {password && (
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'var(--gray-100)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${strength * 25}%`, background: strengthColor, transition: 'all 0.3s' }} />
                </div>
                <span style={{ fontSize: 11, color: strengthColor, fontWeight: 500, width: 40 }}>{strengthLabel}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Re-enter your password"
                style={{ paddingRight: 38 }}
              />
              {confirm && (
                <i className={`ti ${confirm === password ? 'ti-check' : 'ti-x'}`} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: confirm === password ? 'var(--green)' : 'var(--red)', fontSize: 15 }} />
              )}
            </div>
          </div>

          <ul style={{ listStyle: 'none', fontSize: 11, color: 'var(--gray-400)', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {[
              ['At least 8 characters', password.length >= 8],
              ['One uppercase letter', /[A-Z]/.test(password)],
              ['One number', /[0-9]/.test(password)],
            ].map(([label, ok]) => (
              <li key={label} style={{ color: ok ? 'var(--green)' : 'var(--gray-400)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <i className={`ti ${ok ? 'ti-check' : 'ti-minus'}`} style={{ fontSize: 11 }} /> {label}
              </li>
            ))}
          </ul>

          {error && <p className="error-text" style={{ marginBottom: 10 }}>{error}</p>}

          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading || !password || !confirm}>
            {loading ? 'Setting password...' : 'Set Password'}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <Link to="/login" style={{ fontSize: 12, color: 'var(--gray-400)', textDecoration: 'none' }}>
            <i className="ti ti-arrow-left" style={{ fontSize: 11 }} /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
